import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { AiScriptSpreadsheetViewer } from "@/payroll/ai-script/ai-script-spreadsheet-viewer";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";
import type { RecordObject, RecordRef } from "@/profitability/records/objects";
import type { ReportOutput } from "@/profitability/types";
import { useShowSource } from "./sources/source-context";
import { workbookPath } from "./utils";
import { workbookCellRecords, workbookCellSources } from "./workbook-links";
/**
 * The generated workbook, with read-only cells and saved freeze controls. `readOnlySource` points the shared payroll
 * viewer at the report endpoints and turns off everything payroll-specific:
 * editing, scoped reruns, operation recovery and sheet prefetch.
 *
 * Given the period's output and `onOpenRecord`, a cell that names a record
 * (a technician, a job, a business unit) opens it in the page's record sheet.
 * Where the page offers "Show source", a figure read from a file (a column
 * with a `source`) shows where in the file it came from.
 */
export const ReportWorkbook = ({
  businessId,
  runId,
  onAskAiAboutCell,
  output,
  objects,
  onOpenRecord,
}: {
  businessId: string;
  runId: string;
  onAskAiAboutCell?: (context: AiScriptChatSelectionContext) => void;
  /** The output the workbook was built from, to read its cells as records. */
  output?: ReportOutput;
  objects?: RecordObject[];
  onOpenRecord?: (ref: RecordRef) => void;
}) => {
  const [params, setParams] = useSearchParams();
  const recordOf = useMemo(
    () => (output && objects ? workbookCellRecords(output, objects) : null),
    [output, objects],
  );
  const show = useShowSource();
  const sourceOf = useMemo(
    () => (output && objects ? workbookCellSources(output, objects) : null),
    [output, objects],
  );
  return (
    <AiScriptSpreadsheetViewer
      businessId={businessId}
      runId={runId}
      onAskAiAboutCell={onAskAiAboutCell}
      cellLink={
        recordOf && onOpenRecord
          ? (cell) => {
              const record = recordOf(cell);
              return record
                ? {
                    label: record.label,
                    onOpen: () => onOpenRecord(record.ref),
                  }
                : null;
            }
          : undefined
      }
      cellSource={
        sourceOf && show
          ? (cell) => {
              const source = sourceOf(cell);
              const column = source?.object.dataset.columns.find(
                (item) => item.key === source.column,
              );
              return source && show.available(source.object.dataset, column)
                ? () => show.open(source)
                : null;
            }
          : undefined
      }
      fillHeight
      readOnlySource={{
        cacheKey: "profitability-workbook",
        workbookPath: workbookPath(businessId, runId),
      }}
      activeSheetId={params.get("sheet")}
      onActiveSheetChange={(sheet) =>
        setParams(
          (current) => {
            const next = new URLSearchParams(current);
            next.set("sheet", sheet);
            return next;
          },
          { replace: true },
        )
      }
    />
  );
};
